import axios from 'axios';
import * as Sentry from '@sentry/react';

const DEV_VALIDATE_CODE_URL = 'https://cloud-function-test-788603022600.europe-west1.run.app/validateCode';
const DEV_VALIDATE_IAC_URL = 'https://validate-iamsterdam-card-515302875590.europe-west1.run.app/validateIAmsterdamCard';

function requireEnvUrl(key: string, devFallback: string): string {
    const value = import.meta.env[key];
    if (value) return value;
    if (import.meta.env.PROD) {
        const msg = `Missing required env var: ${key}`;
        Sentry.captureMessage(msg, 'error');
        throw new Error(msg);
    }
    return devFallback;
}

const VALIDATE_CODE_URL = requireEnvUrl('VITE_VALIDATE_CODE_URL', DEV_VALIDATE_CODE_URL);
const VALIDATE_IAC_URL = requireEnvUrl('VITE_VALIDATE_IAC_URL', DEV_VALIDATE_IAC_URL);

const REQUEST_TIMEOUT_MS = 15_000;
// The IAC proxy's own upstream budget is 10s (axios timeout in the cloud
// function) plus Cloud Run cold start — a 15s client abort fires while the
// backend may still complete (and ACTIVATE the card), losing the response.
const IAC_REQUEST_TIMEOUT_MS = 20_000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1_500;

export class GateError extends Error {
    reason: string;
    constructor(message: string, reason: string) {
        super(message);
        this.reason = reason;
    }
}

const isRetryable = (err: unknown): boolean => {
    if (!axios.isAxiosError(err)) return true;
    if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') return true;
    const status = err.response?.status;
    return status !== undefined && status >= 500;
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const enterGate = async (code: string) => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            const response = await axios.post(VALIDATE_CODE_URL, { code }, { timeout: REQUEST_TIMEOUT_MS });
            return response.data;
        } catch (err: unknown) {
            lastError = err;
            if (axios.isAxiosError(err) && err.response?.data) {
                const status = err.response.status;
                if (status !== undefined && status >= 400 && status < 500) {
                    const { reason, message } = err.response.data;
                    throw new GateError(message || 'Error entering gate', reason || 'unknown');
                }
            }
            if (attempt < MAX_RETRIES && isRetryable(err)) {
                await delay(RETRY_DELAY_MS * (attempt + 1));
                continue;
            }
            break;
        }
    }

    Sentry.captureException(lastError);
    if (axios.isAxiosError(lastError) && lastError.code === 'ECONNABORTED') {
        throw new GateError('Request timed out', 'network');
    }
    throw new GateError('Error entering gate', 'network');
};

export const validateIAmsterdamCard = async (cardId: string) => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            const response = await axios.post(VALIDATE_IAC_URL, { cardId }, { timeout: IAC_REQUEST_TIMEOUT_MS });
            return response.data;
        } catch (err: unknown) {
            lastError = err;
            if (axios.isAxiosError(err) && err.response?.data) {
                const status = err.response.status;
                const serverMessage = (err.response.data as { message?: string }).message;
                if (status !== undefined && status >= 400 && status < 500) {
                    throw new Error(serverMessage || 'Error validating iamsterdam card');
                }
                // 502 iac_auth_error = OUR token is rejected upstream — a
                // config failure retries can't fix. Fail fast instead of
                // burning retries (each is a real I amsterdam API call).
                if (serverMessage === 'iac_auth_error') {
                    Sentry.captureException(err);
                    throw new Error('iac_auth_error');
                }
            }
            if (attempt < MAX_RETRIES && isRetryable(err)) {
                await delay(RETRY_DELAY_MS * (attempt + 1));
                continue;
            }
            break;
        }
    }

    Sentry.captureException(lastError);
    // Preserve the backend's failure reason (iac_timeout / iac_api_error /
    // internal_error) so AuthContext can show the matching message instead
    // of collapsing every server failure to the generic "unknown".
    if (axios.isAxiosError(lastError)) {
        const serverMessage = (lastError.response?.data as { message?: string } | undefined)?.message;
        if (serverMessage) throw new Error(serverMessage);
        if (lastError.code === 'ECONNABORTED') throw new Error('iac_timeout');
    }
    throw new Error('Error validating iamsterdam card');
};
